import { ActiveUserStore } from '@active-user-management';
import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, isDevMode, OnInit, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SidenavService } from '@app-layout/sidenav/data-access';
import { ActiveUserPermissionsDirective, AuthStore } from '@auth/data-access';
import { CycleListType, PermissionType, UserRoleType } from '@core/api/data-access';
import { CyclesService } from '@cycles/data-access';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { filter, shareReplay, switchMap, take, timer } from 'rxjs';
import { SidenavHeaderComponent } from './components/sidenav-header/sidenav-header.component';
import { SidenavItemComponent } from './components/sidenav-menu/sidenav-item/sidenav-item.component';
import { SidenavItemsGroupComponent } from './components/sidenav-menu/sidenav-items-group/sidenav-items-group.component';
import { SidenavSectionComponent } from './components/sidenav-menu/sidenav-section/sidenav-section.component';
import { SidenavSearchComponent } from './components/sub-components/sidenav-search/sidenav-search.component';
import { SidenavUserProfileComponent } from './components/sub-components/sidenav-user-profile/sidenav-user-profile.component';
import { SidenavVersionInfoComponent } from './components/sub-components/sidenav-version-info/sidenav-version-info.component';

@Component({
	selector: 'dc-sidenav',
	imports: [
		CommonModule,
		RouterModule,
		SidenavHeaderComponent,
		SidenavVersionInfoComponent,
		SidenavUserProfileComponent,
		SidenavSectionComponent,
		SidenavItemComponent,
		SidenavItemsGroupComponent,
		ActiveUserPermissionsDirective,
		TooltipModule,
		TranslateModule,
		SidenavSearchComponent,
	],
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent implements OnInit {
	protected readonly PermissionType = PermissionType;
	protected readonly isDevMode = isDevMode;
	protected readonly location = location;
	protected readonly UserRoleType = UserRoleType;
	protected readonly CycleListType = CycleListType;

	// Services
	protected readonly activeUserStore = inject(ActiveUserStore);
	private readonly authStore = inject(AuthStore);
	private sidenavService = inject(SidenavService);
	private readonly destroyRef = inject(DestroyRef);
	protected readonly cyclesService = inject(CyclesService);
	protected readonly router = inject(Router);

	showProfileDialog = false;

	protected readonly activeUserPermissionsAndRoles$ = computed(() => {
		const activeUserPermissions = this.authStore.activeUserPermissions$();
		const activeUserRoles = this.activeUserStore.user$()?.roles || [];
		return [...activeUserRoles, ...activeUserPermissions];
	});
	protected readonly isCollapsed = computed(() => this.sidenavService.collapsed());
	protected readonly isAdminCompany$: Signal<boolean> = computed(() => this.authStore.isAdmin());

	protected readonly cyclesCountMap$ = signal<Map<CycleListType, number>>(new Map());

	constructor() {}

	ngOnInit(): void {
		this.sidenavService.initFromLocalStorage();
		const isAuth = this.authStore.isAuthenticated();
		const isPermanent = this.authStore.isPermanent();
		if (isAuth && isPermanent) {
			this.router.events
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					filter(event => event instanceof NavigationEnd),
					switchMap(() => timer(1000)),
					switchMap(() => this.cyclesService.List.getCyclesCount().pipe(take(1), shareReplay({bufferSize: 1, refCount: true}))),
				)
				.subscribe(response => {
					this.cyclesCountMap$.set(response);
				});
		}
	}

	closeAllDialogs(): void {
		this.showProfileDialog = false;
	}

	onBackdropClick(): void {
		this.closeAllDialogs();
		// במובייל לא סוגרים את הסיידנב בלחיצה על backdrop - רק פרופיל דיאלוגים
	}

	setCollapsed(value: boolean): void {
		this.sidenavService.setCollapsed(value);
	}

	navTo(path: string) {
		const origin = location.origin;
		const url = new URL(origin);
		url.pathname = path;
		location.href = url.toString();
	}
}
